/**
 * Realtime event mapping.
 *
 * Voice Live speaks the OpenAI-Realtime-compatible protocol. Over WebRTC the
 * non-audio events arrive on the `voice-live-events` data channel (audio itself
 * flows over RTP). This module maps the events needed by the widget onto the
 * provider-neutral events of `@microsoft/voice-widget-core-client`.
 *
 * These minimal shapes mirror `@azure/ai-voicelive`'s server-event types; kept
 * local to avoid pulling the SDK into the browser bundle just for types.
 */

import type { VoiceAgentProviderEvents } from "@microsoft/voice-widget-core-client";

/** A raw Realtime server event as delivered on the data channel. */
export interface RealtimeServerEvent {
  type: string;
  [key: string]: unknown;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/**
 * Translate one Realtime server event into normalized provider events.
 * Unknown/unhandled types still reach `onRawEvent` (called by the caller).
 * Returns nothing; side effects are the `ev.*` callbacks.
 */
export function mapRealtimeEvent(event: RealtimeServerEvent, ev: VoiceAgentProviderEvents): void {
  switch (event.type) {
    // — Turn / mode transitions —
    case "input_audio_buffer.speech_started":
      ev.onModeChange?.("listening");
      break;
    case "input_audio_buffer.speech_stopped":
    case "response.created":
      ev.onModeChange?.("thinking");
      break;
    case "response.output_audio.delta":
    case "response.audio.delta":
    case "response.audio_transcript.delta":
    case "response.output_audio_transcript.delta":
      ev.onModeChange?.("speaking");
      break;
    case "response.done":
      ev.onModeChange?.("listening");
      break;
    default:
      break;
  }

  // Transcript deltas (agent), handled separately so mode + text both fire.
  if (
    event.type === "response.audio_transcript.delta" ||
    event.type === "response.output_audio_transcript.delta"
  ) {
    ev.onTranscript?.({ role: "agent", text: str(event["delta"]), final: false });
  } else if (
    event.type === "response.audio_transcript.done" ||
    event.type === "response.output_audio_transcript.done"
  ) {
    ev.onTranscript?.({ role: "agent", text: str(event["transcript"]), final: true });
  } else if (event.type === "conversation.item.input_audio_transcription.completed") {
    ev.onTranscript?.({ role: "user", text: str(event["transcript"]), final: true });
  } else if (event.type === "error") {
    const err = (event["error"] as Record<string, unknown> | undefined) ?? {};
    ev.onError?.({
      code: str(err["code"]) || "voicelive_error",
      message: str(err["message"]) || "Voice Live runtime error.",
      recoverable: false,
    });
  }
}
